import i18n from '@/utils/i18n';
import moment, { Moment } from 'moment-timezone';
import {
  AvailabilityDayFragment,
  AvailableOrderFulfillmentFieldsFragment,
  EmployeeRole,
  FulfillmentName,
  OrderFulfillmentAvailability,
  OrderStatus,
  OrderSummary_ItemListFieldsFragment,
} from '@/types';
import {
  formatShortDate,
  formatSummaryDate,
  formatTimeslot,
  TIME_FORMAT,
} from '@/utils/date';
import { getTrueTime } from '@/utils/true-time';
import {
  Timeslot,
  TimeslotOption,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';

// Our "Cart" relies on an order in one of these two statuses.
// Extract to a variable so all order queries / actions will be scoped to the
// correct orders
export const UNPLACED_ORDER_STATUSES = [
  OrderStatus.Draft,
  OrderStatus.InReview,
];

export interface TranslationArgs {
  cutoffDay?: string;
  cutoffTime?: string;
  date?: string;
  deliveryDay?: string;
  supplierName?: string;
}

export const areTimeslotsEqual = (
  timeslot1: Timeslot | null,
  timeslot2: Timeslot | null,
) => {
  return (
    timeslot1?.startTime === timeslot2?.startTime &&
    timeslot1?.endTime === timeslot2?.endTime
  );
};

export const AVAILABILITY_DATE_FORMAT = 'YYYY-MM-DD';
const END_OF_DAY_TIME = '23:59:59';

// only override default logic if ADMIN or SUPPLIER_DEFAULT
// https://usepepper.atlassian.net/browse/PROD-175
const ADMIN_VALIDATION_OVERRIDE_ROLES = new Set([
  EmployeeRole.Admin,
  EmployeeRole.SupplierDefault,
]);

export const FULFILLMENT_SHIPPING_TYPES: FulfillmentName[] = [
  FulfillmentName.Ups_2Day,
  FulfillmentName.UpsGround,
  FulfillmentName.UpsNextDay,
];

export const ORDER_FULFILLMENT_TYPE_TO_TRANSLATION_KEY_MAP: {
  [type in FulfillmentName]: string;
} = {
  [FulfillmentName.Delivery]: i18n.t('delivery'),
  [FulfillmentName.Pickup]: i18n.t('pickup'),
  [FulfillmentName.SecondRunDelivery]: i18n.t('second_run_delivery'),
  [FulfillmentName.UpsGround]: i18n.t('ups_ground'),
  [FulfillmentName.UpsNextDay]: i18n.t('ups_next_day'),
  [FulfillmentName.Ups_2Day]: i18n.t('ups_2_day'),
};

export const getOrderFulfillmentDates = (
  orderItems: OrderSummary_ItemListFieldsFragment[],
  desiredFulfillmentTime?: string | null,
) => {
  return [
    ...new Set(
      orderItems.map((item) =>
        formatSummaryDate(
          new Date(
            (item.earliest_fulfillment_at || desiredFulfillmentTime) ?? '',
          ),
        ),
      ),
    ),
  ];
};

/*
 * The supplier's availability row for a day. The moment must be start of day in
 * the restaurant's timezone: the user only ever picks a date, so only its
 * YYYY-MM-DD is matched.
 */
export const findAvailabilityDay = (
  fulfillmentOption: AvailableOrderFulfillmentFieldsFragment | null,
  fulfillmentDateMoment: Moment,
): AvailabilityDayFragment | null => {
  if (!fulfillmentOption) {
    return null;
  }

  const formattedDate = fulfillmentDateMoment.format(AVAILABILITY_DATE_FORMAT);

  return (
    fulfillmentOption.availability.find(
      (availabilityDay) => availabilityDay.date === formattedDate,
    ) ?? null
  );
};

// only override default logic if ADMIN or SUPPLIER_DEFAULT
// https://usepepper.atlassian.net/browse/PROD-175
// additional roles can be configured via dynamic config
const validateAsAdmin = (
  employeeRole: EmployeeRole | null,
  additionalOverrideSupplierRoles: EmployeeRole[] = [],
) => {
  const additionalOverrideRoles = new Set(additionalOverrideSupplierRoles);

  return Boolean(
    employeeRole &&
      (ADMIN_VALIDATION_OVERRIDE_ROLES.has(employeeRole) ||
        additionalOverrideRoles.has(employeeRole)),
  );
};

interface FulfillmentDateRange {
  disabledDates: Moment[];
  earliestDate: Moment;
  fallbackDates: Moment[];
  latestDate: Moment | null;
}

/*
 * Class to use to determine the validity of fulfillment dates.
 *
 * Important info about order dates:
 *
 * restaurant_desired_delivery_time is always saved as midnight on the selected
 * day in the restaurant timezone, and saved as an ISO string. So saving April
 * 8th 2022 from NYC would save '2022-04-08T04:00:00.000Z'
 *
 * For shipping fulfillment types, restaurant_desired_delivery_time is the
 * start of the current day in the restaurant timezone.
 *
 * What we care about is the day the restaurant chose on the calendar,
 * we don't actually care about the time attached to it. So when looking up
 * supplier's fulfillment info using that saved date, we use the restaurant's
 * timezone. This ensures we match on the date the user actually selected.
 * Once found, we only use the supplier's timezone to generate the cutoff time
 * for that date, because the cutoff time is stored in the supplier's timezone.
 *
 * Doing this will avoid issues where the supplier and restaurant are in
 * different timezones.
 *
 * Example:
 *  saved date: '2022-04-08T04:00:00.000Z'
 *  Date / time for restaurant in NYC: 04/08/2022 at midnight
 *  Date / time for supplier in chicago: 04/07/2022 at 11pm
 *  availability info: { date: '2022-04-08', cutoff_time: '10:00:00', lead_time_days: 1}
 *
 * The day the restaurant selected is 04/08. Find the availability for that day.
 * Cutoff time is 10am supplier time, and must be placed 1 day before fulfillment
 * That means cutoff for 4/8 delivery is 4/7 at 10am central
 * Converted to restaurant time, it's 4/7 at 11am eastern.
 *
 * The day/month/year the user chooses is ALWAYS the date we expect the order
 * to be fulfilled. And we assume it will be the same day in the supplier's
 * timezone.
 */
export class OrderAvailability {
  availabilities: AvailableOrderFulfillmentFieldsFragment[];
  fallbackFulfillmentEnabled: boolean;
  fulfillmentType: FulfillmentName;
  timezones: TimeZoneMap;

  constructor(
    availabilities: AvailableOrderFulfillmentFieldsFragment[],
    timezones: TimeZoneMap,
    fulfillmentType: FulfillmentName | null,
    fallbackFulfillmentEnabled = false,
  ) {
    this.availabilities = availabilities;
    this.fallbackFulfillmentEnabled = fallbackFulfillmentEnabled;
    this.timezones = timezones;

    const defaultAvailability =
      availabilities.find(
        (availability) => !availability.primary_fulfillment_settings_id,
      ) ?? availabilities[0];
    const requestedType =
      fulfillmentType ??
      defaultAvailability?.fulfillment_name ??
      FulfillmentName.Delivery;

    /*
     * Keep the type in sync with what `selectedAvailability` resolves to. If
     * the requested type isn't offered, that getter falls back to the first
     * availability, so every date, display name and settings UUID comes from
     * that entry. Reporting the unoffered type here would let callers write it
     * onto the order, which the server then can't resolve settings for.
     */
    const isRequestedTypeOffered = availabilities.some(
      (a) => a.fulfillment_name === requestedType,
    );

    this.fulfillmentType =
      availabilities.length > 0 && !isRequestedTypeOffered
        ? availabilities[0].fulfillment_name
        : requestedType;
  }

  /*
   * creates a moment object by parsing the cutoff time string in the format
   * we expect (hour:minute:second) to be used when setting cutoff times
   */
  getCutoffTimeAsMoment(cutoffTimeString: string) {
    return moment(cutoffTimeString, TIME_FORMAT, true);
  }

  /*
   * Helper method, creates a moment object by parsing the date and time strings
   * in the format we expect (YYYY-MM-DD and hour:minute:second) to be used when
   * setting cutoff times.
   * Returns a moment object in the supplier's timezone.
   */
  getDateTimeInSupplierTimezone(date: string, time: string | null) {
    const parsedMoment = moment(date, AVAILABILITY_DATE_FORMAT);
    let momentForDay = moment()
      .tz(this.timezones.supplier)
      .set({
        date: parsedMoment.date(),
        month: parsedMoment.month(),
        year: parsedMoment.year(),
      })
      .startOf('day');
    const helperMoment = time ? this.getCutoffTimeAsMoment(time) : null;

    if (helperMoment && helperMoment.isValid()) {
      momentForDay.set({
        hours: helperMoment.hours(),
        minutes: helperMoment.minutes(),
        seconds: helperMoment.seconds(),
      });
    } else {
      momentForDay = momentForDay.endOf('day');
    }

    return momentForDay;
  }

  /*
   * Helper method, takes in an Availability day from the
   * GetAvailableOrderFulfillmentResponse and returns a Moment for the cutoff
   * time to place an order for the day, in the supplier's timezone.
   * AvailabilityDayFragment dates and cutoff_times are using the supplier's
   * timezone, which may not match up with local or restaurant time. So convert
   * everything to suppliers time to get correct representation.
   *
   * If there is no cutoff_time, you can order at any day `lead_time_days`
   * before, so set it to endOfDay.
   */
  getCutoffMomentForDay({
    date,
    cutoff_time,
    lead_time_days,
  }: AvailabilityDayFragment) {
    const momentForDay = this.getDateTimeInSupplierTimezone(
      date,
      cutoff_time ?? null,
    );

    // if lead time is not set, assume 1 day
    const backTrackDays =
      typeof lead_time_days === 'number' ? lead_time_days : 1;

    momentForDay.subtract(backTrackDays, 'days');

    return momentForDay;
  }

  /*
   * Helper method, uses the GetAvailableOrderFulfillmentResponse and the
   * selected fulfillment type to return the availability entry for the
   * fulfillment type.
   *
   * If no type is set, uses the first one returned
   */
  get selectedAvailability() {
    if (!this.availabilities.length) {
      return null;
    }

    return (
      this.availabilities.find(
        (a) => a.fulfillment_name === this.fulfillmentType,
      ) || this.availabilities[0]
    );
  }

  get selectedFulfillmentSettingsUUID() {
    return this.selectedAvailability?.fulfillment_settings_id ?? null;
  }

  /*
   * Used in the fulfillment availability header when rendering the display
   * name for the selected fulfillment option.
   */
  get fulfillmentDisplayName() {
    return this.selectedAvailability?.fulfillment_display_name;
  }

  get fulfillmentDisclaimer() {
    return this.selectedAvailability?.fulfillment_disclaimer;
  }

  /*
   * helper method to return the same exact moment value whenever start of day
   * is needed. Important because we use it for both updating and validating
   * fulfillment date for shipping options, so ensure they're both set from
   * the same place
   */
  get startOfTodayMoment() {
    return this.nowMoment.startOf('day');
  }

  /*
   * Helper method, returns the current time in the restaurant's timezone from
   * the true time, so a skewed device clock can't hide days the server still
   * accepts.
   */
  get nowMoment() {
    return moment.tz(getTrueTime(), this.timezones.restaurant);
  }

  /*
   * Helper method to check if a single timeslot is still available for ordering.
   * Orders have to be placed before the start time of the timeslot. There's an
   * optional lead_time_minutes that can be configured for the timeslot settings
   * Only consider lead time if the user is not an admin.
   * Timeslots are stored in supplier timezone
   */
  private isTimeslotAvailable(
    availabilityDay: AvailabilityDayFragment,
    timeslotStartTime: string,
    now: Moment,
    applyLeadTime = true,
  ): boolean {
    let timeslotCutoffMoment = this.getDateTimeInSupplierTimezone(
      availabilityDay.date,
      timeslotStartTime,
    );

    if (applyLeadTime) {
      const leadTimeMinutes =
        availabilityDay.timeslot_settings?.lead_time_minutes || 0;
      if (leadTimeMinutes > 0) {
        timeslotCutoffMoment = timeslotCutoffMoment.subtract(
          leadTimeMinutes,
          'minutes',
        );
      }
    }

    return now.isBefore(timeslotCutoffMoment);
  }

  /*
   * Helper method, returns the timeslot options for the given availability day.
   * Keep timeslot metadata in supplier timezone. Display timslot titles in
   * restaurant timezone.
   *
   * Apply lead time logic to start time of timeslot based on employee role.
   */
  getTimeslotOptionsForDay(
    availabilityDay: AvailabilityDayFragment,
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ) {
    if (!availabilityDay.timeslot_settings) {
      return [];
    }

    const adminOverride = validateAsAdmin(
      supplierEmployeeRole,
      additionalOverrideSupplierRoles,
    );

    const now = this.nowMoment;
    return availabilityDay.timeslot_settings.timeslots.map<TimeslotOption>(
      (timeslot) => {
        const timeslotEntry: Timeslot = {
          endTime: timeslot.end_time,
          startTime: timeslot.start_time,
        };
        const formattedTimeslot = formatTimeslot(
          timeslotEntry,
          this.timezones,
          true,
        );
        return {
          metadata: {
            disabled: !this.isTimeslotAvailable(
              availabilityDay,
              timeslot.start_time,
              now,
              !adminOverride, // Apply lead time unless admin
            ),
            timeslot: timeslotEntry,
          },
          title: formattedTimeslot,
          value: formattedTimeslot,
        };
      },
    );
  }

  getTimeslotOptionsForDate(
    fulfillmentDateMoment: Moment,
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ): TimeslotOption[] {
    const availabilityDay = findAvailabilityDay(
      this.resolveAvailabilityForDate(
        fulfillmentDateMoment,
        supplierEmployeeRole,
        additionalOverrideSupplierRoles,
      ),
      fulfillmentDateMoment,
    );

    return availabilityDay
      ? this.getTimeslotOptionsForDay(
          availabilityDay,
          supplierEmployeeRole,
          additionalOverrideSupplierRoles,
        )
      : [];
  }

  /*
   * Helper method, returns the first available timeslot for the given availability day.
   */
  getFirstAvailableTimeslot(availabilityDay: AvailabilityDayFragment) {
    if (!availabilityDay.timeslot_settings) {
      return null;
    }
    const now = this.nowMoment;
    return (
      availabilityDay.timeslot_settings.timeslots.find(({ start_time }) =>
        this.isTimeslotAvailable(availabilityDay, start_time, now),
      ) || null
    );
  }

  /*
   * Helper method, checks if the given timeslot is valid for the given availability day.
   * Timeslots are stored in supplier timezone
   *
   * For a timeslot to be valid:
   * - there must be a timeslot with matching start_time and end_time
   * - the current time must be before the cutoff time for the timeslot
   * - if the user is an admin, the cutoff time is the start time of the timeslot
   * - if the user is NOT an admin, the cutoff time is lead_time_minutes before the start time
   */
  isValidTimeslotForAvailabilityDay(
    availabilityDay: AvailabilityDayFragment,
    timeslot: Timeslot,
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ) {
    if (!availabilityDay.timeslot_settings) {
      return false;
    }

    const adminOverride = validateAsAdmin(
      supplierEmployeeRole,
      additionalOverrideSupplierRoles,
    );

    const now = this.nowMoment;

    return availabilityDay.timeslot_settings.timeslots.some(
      ({ start_time, end_time }) => {
        const valuesMatch =
          start_time === timeslot.startTime && end_time === timeslot.endTime;

        if (!valuesMatch) {
          return false;
        }

        return this.isTimeslotAvailable(
          availabilityDay,
          start_time,
          now,
          !adminOverride, // Apply lead time unless admin
        );
      },
    );
  }

  /*
   * Helper method, checks if there is ast least one available timeslot for the given availability day.
   * If timeslot selection is not required, return true as there is no timeslot to validate.
   */
  hasAvailableTimeslot(
    availabilityDay: AvailabilityDayFragment,
    now: Moment,
    adminOverride = false,
  ) {
    // If timeslot selection is not required, return true as there is no timeslot to validate.
    if (
      !availabilityDay.timeslot_settings ||
      !availabilityDay.timeslot_settings.selection_required
    ) {
      return true;
    }

    return availabilityDay.timeslot_settings.timeslots.some(({ start_time }) =>
      this.isTimeslotAvailable(
        availabilityDay,
        start_time,
        now,
        !adminOverride, // Apply lead time unless admin
      ),
    );
  }

  /*
   * Gets the AvailabilityDayFragment associated with the next available fulfillment day.
   * If there is no next available fulfillment day, returns null.
   *
   * could be null due to it being a shipping option (where no day selection is required)
   * or due to missing or invalid fulfillment settings
   */
  get nextFulfillmentDayAvailability() {
    if (this.isShippingOption) {
      return null;
    }

    if (
      !this.selectedAvailability ||
      !this.selectedAvailability.availability.length
    ) {
      return null;
    }

    const now = this.nowMoment;
    for (let candidate of this.selectedAvailability.availability) {
      const supplierCutoffMoment = this.getCutoffMomentForDay(candidate);

      if (
        now.isBefore(supplierCutoffMoment) &&
        this.hasAvailableTimeslot(candidate, now)
      ) {
        return candidate;
      }
    }

    return null;
  }

  /*
   * Returns a moment representing the next available fulfillment day to use
   * as the restaurant_desired_delivery_time. Just like the calendar, this
   * returns a value as the start of day in the restaurant's timezone.
   *
   * For shipping types:
   * - fulfillment date is set to the day the order was placed. So always
   *   return start of day in restaurant timezone.
   *
   * For day types:
   * - Loops through the availability days in the
   *   GetAvailableOrderFulfillmentResponse, creates a cutoff time for each day
   *   in the supplier's timezone, and sees if the current time is before that
   *   cutoff time.
   * - If it is, it returns the start of day for the restaurant
   */
  get nextFulfillmentDayMoment() {
    // if selected fulfillment type is shipping, return today
    if (this.isShippingOption) {
      return this.startOfTodayMoment;
    }

    const startOfTomorrow = this.nowMoment.add(1, 'day').startOf('day');

    if (
      !this.selectedAvailability ||
      !this.selectedAvailability.availability.length
    ) {
      return startOfTomorrow;
    }

    const nextFulfillmentDayAvailability = this.nextFulfillmentDayAvailability;
    if (nextFulfillmentDayAvailability) {
      return this.getMomentForAvailabilityDay(nextFulfillmentDayAvailability);
    }

    return startOfTomorrow;
  }

  /*
   * Returns a moment representing the start of day in the restaurant timezone
   * for the given availability day.
   */
  getMomentForAvailabilityDay(availabilityDay: OrderFulfillmentAvailability) {
    return moment(availabilityDay.date)
      .tz(this.timezones.restaurant, true)
      .startOf('day');
  }

  // returns true if selected fulfillment type is shipping option
  // false if it's days type
  get isShippingOption() {
    return FULFILLMENT_SHIPPING_TYPES.includes(this.fulfillmentType);
  }

  // The fallback fulfillment settings of the selected one, none when the flag is off.
  get fallbackAvailabilities() {
    const primaryFulfillmentSettingsUUID =
      this.selectedAvailability?.fulfillment_settings_id;

    return this.fallbackFulfillmentEnabled && primaryFulfillmentSettingsUUID
      ? this.availabilities.filter(
          (availability) =>
            availability.primary_fulfillment_settings_id ===
            primaryFulfillmentSettingsUUID,
        )
      : [];
  }

  /*
   * The fulfillment setting that can serve the date: the selected one first,
   * then the fallback whose cutoff for that date is earliest.
   */
  resolveAvailabilityForDate(
    fulfillmentDateMoment: Moment,
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ): AvailableOrderFulfillmentFieldsFragment | null {
    if (
      this.isValidDateForAvailability(
        this.selectedAvailability,
        fulfillmentDateMoment,
        supplierEmployeeRole,
        additionalOverrideSupplierRoles,
      )
    ) {
      return this.selectedAvailability;
    }

    let earliestFulfillmentOption: AvailableOrderFulfillmentFieldsFragment | null =
      null;
    let earliestCutoff = Infinity;

    for (const availability of this.fallbackAvailabilities) {
      if (
        !this.isValidDateForAvailability(
          availability,
          fulfillmentDateMoment,
          supplierEmployeeRole,
          additionalOverrideSupplierRoles,
        )
      ) {
        continue;
      }

      const availabilityDay = findAvailabilityDay(
        availability,
        fulfillmentDateMoment,
      );
      const cutoff = availabilityDay
        ? this.getCutoffMomentForDay(availabilityDay).valueOf()
        : Infinity;

      if (!earliestFulfillmentOption || cutoff < earliestCutoff) {
        earliestFulfillmentOption = availability;
        earliestCutoff = cutoff;
      }
    }

    return earliestFulfillmentOption;
  }

  private isValidDateForAvailability(
    availability: AvailableOrderFulfillmentFieldsFragment | null,
    fulfillmentDateMoment: Moment,
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ): boolean {
    const adminOverride = validateAsAdmin(
      supplierEmployeeRole,
      additionalOverrideSupplierRoles,
    );

    const fulfillmentDateMomentClone = fulfillmentDateMoment.clone();
    const startOfToday = this.startOfTodayMoment;

    // if the selected fulfillment type is a shipping option, only today is valid
    if (this.isShippingOption) {
      return fulfillmentDateMoment.isSame(startOfToday);
    }

    // If fulfillment date is before start of today
    if (fulfillmentDateMomentClone.isBefore(startOfToday)) {
      return false;
    }

    if (!availability) {
      // if there is no availability, just allow them to order
      return true;
    }

    const foundAvailabilityDate = findAvailabilityDay(
      availability,
      fulfillmentDateMomentClone,
    );

    // if they don't fulfill on that day, only allow employees to order
    if (!foundAvailabilityDate) {
      // potentially an issue here where we allow same day if not enabled
      // for supplier employees
      return adminOverride;
    }

    const foundLeadTimeDays =
      typeof foundAvailabilityDate.lead_time_days === 'number'
        ? foundAvailabilityDate.lead_time_days
        : 1;

    const now = this.nowMoment;
    // only allow admins to order same day if same day isn't enabled
    if (
      fulfillmentDateMomentClone.isSame(now, 'day') &&
      foundLeadTimeDays > 0
    ) {
      return adminOverride;
    }

    const supplierCutoffTimeForDate = this.getCutoffMomentForDay(
      foundAvailabilityDate,
    );

    const isBeforeRegularCutoffTime = now.isBefore(supplierCutoffTimeForDate);
    // Day is unavailable if after regular cutoff time, and user is not an admin
    if (!isBeforeRegularCutoffTime && !adminOverride) {
      return false;
    }

    // Day is valid if timeslots selection not required, or there is at least one timeslot available.
    return this.hasAvailableTimeslot(foundAvailabilityDate, now, adminOverride);
  }

  /*
   * Used to determine if saved dates are still valid on the order summary
   * screen and the order context.
   *
   * Takes a Moment, which is in the restaurant's timezone, and returns true
   * if the supplier can fulfill on that day.
   *
   * Uses the YYYY-MM-DD the restaurant selected in their timezone, because
   * that's the day they expect fulfillment, to find the availability day in
   * the supplier's fulfillment array. Then generates a cutoff time for that
   * day, and checks if the current time is before the cutoff time.
   *
   * Some edge cases:
   * - if there happens to be no availability at all, just allow them to order.
   * - We do allow supplier users with ADMIN or SUPPLIER_DEFAULT roles to place
   *   orders on days that aren't available, and past cutoff times. Additional
   *   roles can be configured as well via supplier dynamic config setting
   *   'additional_order_validation_supplier_roles'.
   * - For shipping types, we always set the desired fulfillment date to start
   *   of day. So if it is not the start of day in restaurant time, it's invalid
   * - Admins cannot order on a day with required timeslot selection that has no
   *   timeslots available. However, the lead time minutes is ignored for admins.
   *
   */
  isValidSupplierFulfillmentDate(
    fulfillmentDateMoment: Moment,
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ) {
    return (
      this.isValidDateForAvailability(
        this.selectedAvailability,
        fulfillmentDateMoment,
        supplierEmployeeRole,
        additionalOverrideSupplierRoles,
      ) ||
      this.fallbackAvailabilities.some((availability) =>
        this.isValidDateForAvailability(
          availability,
          fulfillmentDateMoment,
          supplierEmployeeRole,
          additionalOverrideSupplierRoles,
        ),
      )
    );
  }

  /*
   * Used in fulfillment availability header to render the cutoff time for the
   * selected date string, which is the saved (or next available) desired
   * delivery time.
   *
   * selectedDateString is always saved as midnight on the selected day in the
   * restaurants timezone, and saved as an ISO string. So saving April 8th 2022
   * from NYC would save '2022-04-08T04:00:00.000Z'
   * When mapping to the supplier's availability, use the restaurant timezone
   * to get the YYYY-MM-DD format. Only use supplier timezone when getting the
   * cutoff time, and convert that cutoff time back to the user's timezone for
   * accurate display.
   *
   * The selectedDateString is not always a valid delivery date according to
   * order fulfillments (for example, supplier employee ordering on unavailable
   * fulfillment days). In this case, use the default setting
   *
   * Example:
   *   saved date: '2022-04-08T04:00:00.000Z'
   *   Date / time for restaurant in NYC: 04/08/2022 at midnight
   *   Date / time for supplier in chicago: 04/07/2022 at 11pm
   *   availability date: {date: '2022-04-08', cutoff_time: '10:00:00', lead_time_days: 1}
   *
   *   1: Use restaurant timezone to find availability date
   *   2: Use supplier timezone to create cutoff time using the selected date,
   *      the supplier's cutoff time, and the lead time days (04/07/2022 at 10:00:00 central)
   *   3: convert to user timezone to show an accurate time on device
   *      (04/07/2022 at 08:00:00 pacific)
   */
  getCutoffForDate(selectedDateString?: string, short = false) {
    if (!selectedDateString || !this.selectedAvailability) {
      return '';
    }

    // create moment from string. Moment is in restaurant timezone,
    // but make it explicit
    const selectedDateMoment = moment(selectedDateString).tz(
      this.timezones.restaurant,
    );

    // use day the restaurant selected to look up from availabilities
    let foundDate = findAvailabilityDay(
      this.selectedAvailability,
      selectedDateMoment,
    );

    // there is no cutoff time if the available date has no cutoff time
    if (foundDate && !foundDate.cutoff_time) {
      return '';
    }
    if (!foundDate) {
      // there is no cutoff time for shipping options or there is no default cutoff time
      if (
        this.isShippingOption ||
        !this.selectedAvailability.default_cutoff_time
      ) {
        return '';
      }
      foundDate = {
        cutoff_time: this.selectedAvailability.default_cutoff_time,
        date: selectedDateMoment.format(AVAILABILITY_DATE_FORMAT),
        lead_time_days: this.selectedAvailability.default_lead_time_days,
        timeslot_settings: null,
      };
    }

    const supplierCutoffTimeMoment = this.getCutoffMomentForDay(foundDate);
    const cutoffTimeFormat =
      moment.locale() === 'zh-cn'
        ? supplierCutoffTimeMoment.minute() === 0
          ? 'ah点' // 凌晨1点
          : 'ah点mm分' // 凌晨1点30分
        : supplierCutoffTimeMoment.minute() === 0
          ? 'ha' // 1AM
          : 'h:mma'; // 1:30AM

    // convert to user time before displaying
    const userTimezone = moment.tz.guess();
    const cutoffTimePart = supplierCutoffTimeMoment
      .tz(userTimezone)
      .format(cutoffTimeFormat);
    const cutoffParts = [cutoffTimePart];

    const orderByDayMoment = supplierCutoffTimeMoment.clone();
    const startOfOrderByDay = orderByDayMoment.clone().startOf('day');
    const todayMoment = moment(getTrueTime()).tz(userTimezone).startOf('day');
    const tomorrowMoment = moment(getTrueTime())
      .tz(userTimezone)
      .add(1, 'day')
      .startOf('day');

    const isToday = startOfOrderByDay.isSame(todayMoment);
    const isTomorrow = startOfOrderByDay.isSame(tomorrowMoment);

    if (short) {
      if (!isToday) {
        cutoffParts.push(formatShortDate(orderByDayMoment));
      }
    } else {
      if (isToday) {
        cutoffParts.push(i18n.t('today'));
      } else if (isTomorrow) {
        cutoffParts.push(i18n.t('tomorrow'));
      } else {
        cutoffParts.push(formatShortDate(orderByDayMoment));
      }
    }

    return cutoffParts.join(' ');
  }

  /*
   * Used in the fulfillment availability header to render the order minimum
   * Order minimums may differ by fulfillment type and fulfillment date
   */
  getOrderMinDisplayStringForDate(selectedDateString?: string) {
    if (selectedDateString) {
      // create moment from string. Moment is in restaurant timezone,
      // but make it explicit
      const selectedDateMoment = moment(selectedDateString).tz(
        this.timezones.restaurant,
      );

      // use day the restaurant selected to look up from availabilities
      const availability = findAvailabilityDay(
        this.selectedAvailability,
        selectedDateMoment,
      );
      if (availability?.order_minimum_display_string) {
        return availability.order_minimum_display_string;
      }
    }

    return this.selectedAvailability?.order_minimum_display_string ?? '';
  }

  /*
   * Gets the soonest availability date for variant packs with lead_time_days
   * and lead_time_cutoff_time set.
   * Does not return a date string if it's the same as the selected date.
   *
   * It is possible for items to have cutoff times that are AFTER default
   * cutoff times. For these cases, we always take the cutoff time for the item
   *
   * For example:
   * Current Date: 4/1 11AM
   * Default fulfillment: 10am cutoff for next day
   * Item level lead time: 2 days, 1pm cutoff time
   *
   * - The user can still order the lead time item for 4/3 (until 1pm) after
   *   1pm, the user can order that lead time item for 4/4
   * - The user can no longer place an order for 4/2 because the next day
   *   cutoff is past
   *
   * The day that gets formatted is based on the restaurant's timezone, as it's
   * being used for display to a restaurant user.
   *
   * The time being passed in as cutoffTime is in the supplier's timezone, as
   * it's stored in the database for the supplier.
   *
   * Selected date is passed in as the ISO string representing the beginning of
   * the day in the restaurant's timezone. We format the date in restaurant's
   * timezone to see if it matches the one we'd be returning.
   *
   * Use the supplier timezone to determine if it's past cutoff time, but
   * convert back to restaurant time when displaying
   */
  getEarliestFulfillmentForLeadTimeItem(
    leadTimeDays: number | null,
    leadTimeCutoffTime: string | null,
    selectedFulfillmentDateString: string | null,
  ) {
    if (!leadTimeDays) {
      return null;
    }

    if (!this.selectedAvailability) {
      return null;
    }

    const now = this.nowMoment;

    for (let candidate of this.selectedAvailability.availability) {
      const leadTimeCutoffMoment = moment(candidate.date)
        .tz(this.timezones.supplier, true)
        .startOf('day');

      const cutoffTimeMoment = this.getCutoffTimeAsMoment(
        leadTimeCutoffTime ?? candidate.cutoff_time ?? END_OF_DAY_TIME,
      );
      if (cutoffTimeMoment?.isValid()) {
        leadTimeCutoffMoment.set({
          hours: cutoffTimeMoment.hours(),
          minutes: cutoffTimeMoment.minutes(),
          seconds: cutoffTimeMoment.seconds(),
        });
      }

      const defaultLeadTimeDays =
        typeof candidate.lead_time_days === 'number'
          ? candidate.lead_time_days
          : 1;
      const maxLeadTime = Math.max(leadTimeDays, defaultLeadTimeDays);

      leadTimeCutoffMoment.subtract(maxLeadTime, 'days');

      if (now.isBefore(leadTimeCutoffMoment)) {
        // candidate date is the next date the item can be fulfilled
        // do not show label if selected date is the same or after this date
        const momentForRestaurant = moment(candidate.date).tz(
          this.timezones.restaurant,
        );

        const selectedFulfillmentMoment = selectedFulfillmentDateString
          ? moment(selectedFulfillmentDateString).tz(this.timezones.restaurant)
          : null;

        if (selectedFulfillmentMoment?.isSameOrAfter(momentForRestaurant)) {
          return null;
        }

        return formatSummaryDate(momentForRestaurant);
      }
    }

    return null;
  }

  /*
   * Returns the fromatted date and timeslot to use in fulfillment buttons.
   * If there is a timeslot, convert from the stored supplier timezone to the
   * restaurant timezone for display.
   */
  getSelectedFulfillmentDateTimeString(
    fulfillmentDate: string,
    timeslot: Timeslot | null,
  ) {
    const formattedDate = formatSummaryDate(new Date(fulfillmentDate));
    if (!timeslot) {
      return formattedDate;
    }

    const formattedTimeslot = formatTimeslot(timeslot, this.timezones);

    return `${formattedDate} ${formattedTimeslot}`;
  }

  /*
   * Returns the selectable range for a fulfillment date picker, for pickers
   * that take a list of excluded dates rather than a predicate.
   *
   * Availability is a finite list of dates, so the range ends at the last date
   * any fulfillment setting of the family publishes. latestDate is null when the range has no
   * end, which is the case for the supplier roles allowed to order past the
   * availability window.
   *
   * disabledDates are the days within the range that nothing can fulfill, and
   * fallbackDates are the days that only a fallback can reach. Like the
   * calendar, every moment returned is the start of day in the restaurant's
   * timezone.
   */
  getFulfillmentDateRange(
    supplierEmployeeRole: EmployeeRole | null = null,
    additionalOverrideSupplierRoles: EmployeeRole[] = [],
  ): FulfillmentDateRange {
    const earliestDate = this.startOfTodayMoment;
    const rangeEnd = [
      ...(this.selectedAvailability ? [this.selectedAvailability] : []),
      ...this.fallbackAvailabilities,
    ].reduce((latest, availability) => {
      const lastDay =
        availability.availability[availability.availability.length - 1];
      const lastDate = lastDay
        ? this.getMomentForAvailabilityDay(lastDay)
        : latest;

      return lastDate.isAfter(latest) ? lastDate : latest;
    }, earliestDate);

    const disabledDates: Moment[] = [];
    const fallbackDates: Moment[] = [];
    const cursor = earliestDate.clone();
    const isDisabled = (date: Moment) =>
      !this.isValidSupplierFulfillmentDate(
        date,
        supplierEmployeeRole,
        additionalOverrideSupplierRoles,
      );

    while (!cursor.isAfter(rangeEnd, 'day')) {
      if (isDisabled(cursor)) {
        disabledDates.push(cursor.clone());
      } else if (
        this.resolveAvailabilityForDate(
          cursor,
          supplierEmployeeRole,
          additionalOverrideSupplierRoles,
        ) !== this.selectedAvailability
      ) {
        fallbackDates.push(cursor.clone());
      }

      cursor.add(1, 'day');
    }

    return {
      disabledDates,
      earliestDate,
      fallbackDates,
      latestDate: isDisabled(rangeEnd.clone().add(1, 'day')) ? rangeEnd : null,
    };
  }
}

export const cartHasLeadTimeItem = (
  orderItems: {
    earliest_fulfillment_at?: string | null;
    variants_pack: { uuid: string };
  }[],
  variantPackHasLeadTimePolicy?: (variantPackUUID: string) => boolean,
) =>
  orderItems.some(
    (orderItem) =>
      Boolean(orderItem.earliest_fulfillment_at) ||
      Boolean(variantPackHasLeadTimePolicy?.(orderItem.variants_pack.uuid)),
  );

export interface ValidFulfillmentValue {
  date: Moment;
  fulfillmentOption: AvailableOrderFulfillmentFieldsFragment;
}

export const getValidFulfillment = ({
  additionalOverrideSupplierRoles = [],
  availabilities,
  hasLeadTimeItem = false,
  savedFulfillmentDateMoment,
  savedFulfillmentOption,
  supplierEmployeeRole = null,
  timezones,
}: {
  additionalOverrideSupplierRoles?: EmployeeRole[];
  availabilities: AvailableOrderFulfillmentFieldsFragment[];
  hasLeadTimeItem?: boolean;
  savedFulfillmentDateMoment: Moment | null;
  savedFulfillmentOption: AvailableOrderFulfillmentFieldsFragment;
  supplierEmployeeRole?: EmployeeRole | null;
  timezones: TimeZoneMap;
}): ValidFulfillmentValue => {
  const primaryFulfillmentOption =
    availabilities.find(
      (availability) =>
        availability.fulfillment_settings_id ===
        savedFulfillmentOption.primary_fulfillment_settings_id,
    ) ?? savedFulfillmentOption;
  // A lead time item needs the supplier warned in advance, which a fallback
  // cannot honour, so those carts are held to the primary's own schedule.
  const orderAvailability = new OrderAvailability(
    hasLeadTimeItem ? [primaryFulfillmentOption] : availabilities,
    timezones,
    primaryFulfillmentOption.fulfillment_name,
    true,
  );
  const resolvedFulfillmentOption = savedFulfillmentDateMoment
    ? orderAvailability.resolveAvailabilityForDate(
        savedFulfillmentDateMoment,
        supplierEmployeeRole,
        additionalOverrideSupplierRoles,
      )
    : null;

  return savedFulfillmentDateMoment && resolvedFulfillmentOption
    ? {
        date: savedFulfillmentDateMoment,
        fulfillmentOption: resolvedFulfillmentOption,
      }
    : {
        date: orderAvailability.nextFulfillmentDayMoment,
        fulfillmentOption: primaryFulfillmentOption,
      };
};

export const isFulfillmentStale = ({
  validFulfillment,
  savedFulfillmentDate,
  savedFulfillmentType,
}: {
  savedFulfillmentDate: string | null | undefined;
  savedFulfillmentType: FulfillmentName | null | undefined;
  validFulfillment: ValidFulfillmentValue;
}) =>
  !savedFulfillmentDate ||
  !validFulfillment.date.isSame(moment(savedFulfillmentDate)) ||
  validFulfillment.fulfillmentOption.fulfillment_name !== savedFulfillmentType;
